import "mapbox-gl/dist/mapbox-gl.css";

import type { FeatureCollection } from "geojson";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Layer,
  Map as ReactMapboxGL,
  NavigationControl,
  Source,
  type MapMouseEvent,
  type MapRef,
} from "react-map-gl/mapbox";

import { MAPBOX_TOKEN } from "@/env";
import { useRoom } from "@/hooks/use-room";
import CustomLayers from "./layers";
import CustomPopups from "./popups";

export default function Map() {
  const { currentBlockTree, currentRoom, cursors, setMyCursor } = useRoom();
  const mapRef = useRef<MapRef>(null);
  const [loaded, setLoaded] = useState(false);
  const [openPopups, setOpenPopups] = useState<Set<string | number>>(new Set());
  const [viewState, setViewState] = useState({
    longitude: 3.79,
    latitude: 51.33,
    zoom: 7.35,
  });

  const togglePopup = (featureId: string | number) => {
    setOpenPopups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(featureId)) {
        newSet.delete(featureId);
      } else {
        newSet.add(featureId);
      }
      return newSet;
    });
  };

  const closePopup = (featureId: string | number) => {
    setOpenPopups((prev) => {
      const newSet = new Set(prev);
      newSet.delete(featureId);
      return newSet;
    });
  };

  const handleMapClick = (event: MapMouseEvent) => {
    if (event.features && event.features.length > 0) {
      event.features.forEach((feature) => {
        if (feature?.id) {
          togglePopup(feature.id);
        }
      });
    }
  };

  const liveCursors: FeatureCollection = useMemo(() => {
    return {
      type: "FeatureCollection",
      features: Array.from(cursors.entries()).map(([clientId, cursor]) => ({
        type: "Feature",
        geometry: {
          type: "Polygon",
          coordinates: [
            [
              [cursor.viewBounds.west, cursor.viewBounds.north],
              [cursor.viewBounds.east, cursor.viewBounds.north],
              [cursor.viewBounds.east, cursor.viewBounds.south],
              [cursor.viewBounds.west, cursor.viewBounds.south],
              [cursor.viewBounds.west, cursor.viewBounds.north],
            ],
          ],
        },
        properties: {
          "map::label": `User ${clientId.slice(0, 5)}`,
          userId: clientId,
        },
      })),
    };
  }, [cursors]);

  const geoDocument = useMemo(() => {
    if (!currentBlockTree) return null;
    return currentBlockTree.toGeoJSON();
  }, [currentBlockTree]);

  useEffect(() => {
    if (!loaded || !mapRef.current) return;

    const bounds = mapRef.current.getBounds();
    if (!bounds) return;

    const viewBounds = {
      north: bounds.getNorth(),
      south: bounds.getSouth(),
      east: bounds.getEast(),
      west: bounds.getWest(),
    };

    setMyCursor(viewState.longitude, viewState.latitude, viewBounds);
  }, [viewState, setMyCursor, loaded]);

  return (
    <>
      <div className="absolute w-full h-full top-0 left-0 z-0">
        <ReactMapboxGL
          ref={mapRef}
          {...viewState}
          mapboxAccessToken={MAPBOX_TOKEN}
          onMove={(evt) => setViewState(evt.viewState)}
          onLoad={() => setLoaded(true)}
          projection="globe"
          mapStyle="mapbox://styles/mapbox/standard"
          interactiveLayerIds={["document-circles"]}
          onClick={handleMapClick}
        >
          {loaded && currentRoom && (
            <Source id="live-cursors" type="geojson" data={liveCursors}>
              <Layer
                type="line"
                paint={{
                  "line-color": "#007acc",
                  "line-width": 2,
                  "line-opacity": 0.8,
                }}
              />
              {
                <Layer
                  id="cursor-labels"
                  type="symbol"
                  source="live-cursors"
                  layout={{
                    "text-field": ["get", "map::label"],
                    "text-offset": [0, 1.25],
                    "text-anchor": "bottom",
                  }}
                />
              }
            </Source>
          )}
          {loaded && geoDocument && (
            <Source
              lineMetrics={false}
              id="document"
              type="geojson"
              data={geoDocument}
            >
              <CustomLayers source="document" />
              <CustomPopups
                data={geoDocument}
                openPopups={openPopups}
                onClosePopup={closePopup}
              />
            </Source>
          )}
          <NavigationControl position="top-right" />
        </ReactMapboxGL>
      </div>
      <div className="absolute bottom-5 left-5 z-1 text-white text-shadow-2xs font-bold">
        HUD: {viewState.latitude.toFixed(2)}, {viewState.longitude.toFixed(2)}{" "}
        <br />
        Zoom: {viewState.zoom.toFixed(2)}
      </div>
    </>
  );
}
